import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Grid,
    CircularProgress,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    ToggleButton,
    ToggleButtonGroup,
    InputAdornment,
    Badge,
    Collapse,
    IconButton,
    TablePagination,
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Search as SearchIcon,
    FilterList as FilterIcon,
    Clear as ClearIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    ListAlt as ListAltIcon,
    ChevronRight as ChevronRightIcon,
    ArrowUpward as ArrowUpIcon,
    ArrowDownward as ArrowDownIcon,
} from '@mui/icons-material';
import { PageHeader, EmptyState } from '../../components/admin';
import { useSearchUdalostiQuery, useUdalostiTypesQuery } from '../../api/JsdhNotifikaceService/service/AdminMutations';
import type { Udalost } from '../../api/JsdhNotifikaceService/generated/models/Udalost';
import { formatAdresa } from '../../utils/adresaUdalosti';

// Helper to get date strings
const getDateString = (daysAgo: number = 0): string => {
    const date = new Date();
    date.setDate(date.getDate() - daysAgo);
    return date.toISOString().split('T')[0];
};

const getTypColor = (pred?: string): 'error' | 'info' | 'warning' | 'default' => {
    switch (pred?.toUpperCase()) {
        case 'POZAR': return 'error';
        case 'TFA': return 'info';
        case 'DN': return 'warning';
        default: return 'default';
    }
};

interface UdalostCardProps {
    udalost: Udalost;
    onClick: () => void;
}

const UdalostCard: React.FC<UdalostCardProps> = ({ udalost, onClick }) => (
    <Paper
        variant="outlined"
        onClick={onClick}
        sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            cursor: 'pointer',
            transition: 'background-color 0.15s',
            '&:hover': { bgcolor: 'action.hover' },
            borderLeft: 4,
            borderLeftColor: udalost.aktivni ? 'success.main' : 'grey.300',
        }}
    >
        {/* Datum + čas */}
        <Box sx={{ flexShrink: 0, minWidth: 55 }}>
            <Typography variant="body2" fontWeight="medium" noWrap>
                {new Date(udalost.casVyhlaseni).toLocaleDateString('cs-CZ')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
                {new Date(udalost.casVyhlaseni).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })}
            </Typography>
        </Box>

        {/* Typ události */}
        {udalost.pred && (
            <Chip
                label={udalost.pred.toUpperCase()}
                color={getTypColor(udalost.pred)}
                size="small"
                sx={{ fontWeight: 'bold', minWidth: 60, flexShrink: 0 }}
            />
        )}

        {/* Název + místo */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body1" fontWeight="bold" noWrap>
                {udalost.udalost}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
                {formatAdresa(udalost)}
            </Typography>
        </Box>

        {/* Stav + šipka */}
        <Chip
            label={udalost.aktivni ? 'Aktivní' : 'Ukončená'}
            color={udalost.aktivni ? 'success' : 'default'}
            size="small"
            variant={udalost.aktivni ? 'filled' : 'outlined'}
            sx={{ flexShrink: 0 }}
        />
        <ChevronRightIcon sx={{ color: 'text.disabled', flexShrink: 0 }} />
    </Paper>
);

const SeznamUdalostiPage: React.FC = () => {
    const navigate = useNavigate();
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [filters, setFilters] = useState({
        dateFrom: '',
        dateTo: '',
        type: '',
        query: '',
        aktivni: '' as '' | 'true' | 'false',
    });
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
    const [sortBy, setSortBy] = useState('casVyhlaseni');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

    const { data: eventTypes } = useUdalostiTypesQuery();

    const {
        data: udalosti,
        isLoading,
        refetch,
    } = useSearchUdalostiQuery({
        ...filters,
        page,
        size: pageSize,
        dateFrom: filters.dateFrom || undefined,
        dateTo: filters.dateTo || undefined,
        type: filters.type || undefined,
        query: filters.query || undefined,
        aktivni: filters.aktivni === '' ? undefined : filters.aktivni === 'true',
        sortBy,
        sortOrder,
    });

    // Count active filters
    const activeFilterCount = useMemo(() => {
        let count = 0;
        if (filters.dateFrom) count++;
        if (filters.dateTo) count++;
        if (filters.type) count++;
        if (filters.query) count++;
        if (filters.aktivni !== '') count++;
        return count;
    }, [filters]);

    // Quick date filter handlers
    const handleQuickDateFilter = (preset: 'today' | 'week' | 'month' | 'all') => {
        switch (preset) {
            case 'today':
                setFilters(f => ({ ...f, dateFrom: getDateString(0), dateTo: getDateString(0) }));
                break;
            case 'week':
                setFilters(f => ({ ...f, dateFrom: getDateString(7), dateTo: getDateString(0) }));
                break;
            case 'month':
                setFilters(f => ({ ...f, dateFrom: getDateString(30), dateTo: getDateString(0) }));
                break;
            case 'all':
                setFilters(f => ({ ...f, dateFrom: '', dateTo: '' }));
                break;
        }
    };

    const clearAllFilters = () => {
        setFilters({ dateFrom: '', dateTo: '', type: '', query: '', aktivni: '' });
    };

    const toggleSort = (field: string) => {
        if (sortBy === field) {
            setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('desc');
        }
    };

    const SortButton: React.FC<{ field: string; label: string }> = ({ field, label }) => (
        <Chip
            label={label}
            size="small"
            variant={sortBy === field ? 'filled' : 'outlined'}
            onClick={() => toggleSort(field)}
            onDelete={sortBy === field ? () => toggleSort(field) : undefined}
            deleteIcon={sortBy === field ? (sortOrder === 'desc' ? <ArrowDownIcon fontSize="small" /> : <ArrowUpIcon fontSize="small" />) : undefined}
            sx={{ cursor: 'pointer' }}
        />
    );

    return (
        <Box>
            <PageHeader
                title="Seznam událostí"
                action={
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={() => refetch()}
                    >
                        Obnovit
                    </Button>
                }
            />

            {/* Search and Quick Filters */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    {/* Text Search */}
                    <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                            placeholder="Hledat v události nebo obci..."
                            fullWidth
                            size="small"
                            value={filters.query}
                            onChange={(e) => setFilters({ ...filters, query: e.target.value })}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="action" />
                                    </InputAdornment>
                                ),
                                endAdornment: filters.query && (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            onClick={() => setFilters({ ...filters, query: '' })}
                                        >
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>

                    {/* Quick Date Filters */}
                    <Grid size={{ xs: 12, md: 4 }}>
                        <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={
                                filters.dateFrom === getDateString(0) && filters.dateTo === getDateString(0) ? 'today' :
                                filters.dateFrom === getDateString(7) && filters.dateTo === getDateString(0) ? 'week' :
                                filters.dateFrom === getDateString(30) && filters.dateTo === getDateString(0) ? 'month' :
                                !filters.dateFrom && !filters.dateTo ? 'all' : null
                            }
                            onChange={(_, value) => value && handleQuickDateFilter(value)}
                        >
                            <ToggleButton value="today">Dnes</ToggleButton>
                            <ToggleButton value="week">Týden</ToggleButton>
                            <ToggleButton value="month">Měsíc</ToggleButton>
                            <ToggleButton value="all">Vše</ToggleButton>
                        </ToggleButtonGroup>
                    </Grid>

                    {/* Status Filter */}
                    <Grid size={{ xs: 12, md: 2 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Stav</InputLabel>
                            <Select
                                value={filters.aktivni}
                                label="Stav"
                                onChange={(e) => setFilters({ ...filters, aktivni: e.target.value as '' | 'true' | 'false' })}
                            >
                                <MenuItem value="">Vše</MenuItem>
                                <MenuItem value="true">Aktivní</MenuItem>
                                <MenuItem value="false">Ukončené</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Advanced Filters Toggle */}
                    <Grid size={{ xs: 12, md: 2 }}>
                        <Button
                            variant="outlined"
                            fullWidth
                            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                            endIcon={showAdvancedFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            startIcon={
                                <Badge badgeContent={activeFilterCount} color="primary">
                                    <FilterIcon />
                                </Badge>
                            }
                        >
                            Filtry
                        </Button>
                    </Grid>
                </Grid>

                {/* Advanced Filters (Collapsible) */}
                <Collapse in={showAdvancedFilters}>
                    <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <TextField
                                    label="Od data"
                                    type="date"
                                    fullWidth
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    value={filters.dateFrom}
                                    onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <TextField
                                    label="Do data"
                                    type="date"
                                    fullWidth
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                    value={filters.dateTo}
                                    onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Typ události</InputLabel>
                                    <Select
                                        value={filters.type}
                                        label="Typ události"
                                        onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                                    >
                                        <MenuItem value="">Všechny typy</MenuItem>
                                        {eventTypes?.map((type) => (
                                            <MenuItem key={type} value={type}>
                                                {type}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    onClick={clearAllFilters}
                                    fullWidth
                                    startIcon={<ClearIcon />}
                                    disabled={activeFilterCount === 0}
                                >
                                    Vymazat filtry ({activeFilterCount})
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                </Collapse>
            </Paper>

            {/* Sort chips */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">Řadit:</Typography>
                <SortButton field="casVyhlaseni" label="Čas" />
                <SortButton field="udalost" label="Událost" />
                <SortButton field="obec" label="Obec" />
            </Box>

            {/* Events List */}
            {isLoading ? (
                <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                </Box>
            ) : (udalosti?.content?.length || 0) > 0 ? (
                <>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {udalosti!.content.map((udalost) => (
                            <UdalostCard
                                key={udalost.id}
                                udalost={udalost}
                                onClick={() => navigate(`/admin/udalosti/${udalost.id}`)}
                            />
                        ))}
                    </Box>
                    <TablePagination
                        component="div"
                        count={udalosti?.totalElements || 0}
                        page={page}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        rowsPerPage={pageSize}
                        onRowsPerPageChange={(e) => {
                            setPageSize(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                        rowsPerPageOptions={[10, 20, 50, 100]}
                        labelRowsPerPage="Na stránce:"
                        labelDisplayedRows={({ from, to, count }) => `${from}–${to} z ${count}`}
                    />
                </>
            ) : (
                <EmptyState
                    icon={<ListAltIcon />}
                    title="Žádné události"
                    description="Události se vytvoří po vyhlášení poplachu."
                />
            )}
        </Box>
    );
};

export default SeznamUdalostiPage;
